import { useMemo } from 'react';
import {
  FlexColumn,
  ModelInput,
  ModelForm,
  ModelSelect,
  RadioYesNoInput,
  EmptyInput,
  ModelNewNumericInput,
  Collapsible,
  SelectInputField,
  SelectInputIcon,
  InputLabel,
  ModelSelectSearchInput,
} from '@equifinance/shared';
import styled, { css } from 'styled-components';
import { omit } from 'lodash';
import { isNonEmptyNumber } from '@equifinance/shared/utils';
import { useLenderOptions } from '../../../../models/lendersModel';
import {
  CreditItem,
  CreditTypes,
  MortgageType,
  RepaymentVehicleType,
  ConsolidationType,
  AddressTypes,
  useCreditContext,
} from '../../../../contexts/credit';
import {
  CreditDetailsContainer,
  CreditDetailsRow,
  CreditDetailsRowHighlight,
  CreditDivider,
  DetailsTitle,
  EquifaxAccordionHeaderShadow,
  ManualPaymentHistoryContainer,
} from '../CreditDetails';
import EquifaxDetails from '../EquifaxDetails/EquifaxDetails';
import ConsolidationDetails from '../../Consolidation/ConsolidationDetails/ConsolidationDetails';
import { HighlightInputGroup } from '../../../Account/components/HighlightInputGroup';
import { withExpandableRowErrors } from '../../../Errors/ExpandableRowErrors';

const Container = styled(FlexColumn)` ${({ theme }) => ` 
  gap:${theme.padding[4]};
  padding:${theme.padding[4]};
  background: ${theme.colours.neutral[9]};
`}`;

const collapsibleWrapperCss = css`
  padding: 0;
`.toString();

const SecuredRowDetails = styled(CreditDetailsRow)`${({ theme }) => `
  ${SelectInputField}[disabled] {
    background-color: transparent;
    color: ${theme.colours.neutral[6]};
    border: 1px solid ${theme.colours.neutral[6]};
  }

  ${SelectInputIcon} {
    color: ${theme.colours.neutral[4]};
  }
  
  ${InputLabel}[disabled] {
    color: ${theme.colours.neutral[6]};
  }
`}`;

export function SecuredCreditDetails({
  addressType,
  data: creditItem,
}: {
  addressType: AddressTypes,
  data: CreditItem,
}) {
  const {
    credit,
    updateCreditItem,
  } = useCreditContext();

  const {
    errors,
    warnings,
  } = credit;

  const lenders = useLenderOptions('secured');
  const lenderOptions = useMemo(() => {
    const hasOtherOption = creditItem?.isEquifax === false && creditItem?.accountType !== 'FirstCharge';
    const options = { ...lenders };
    if (!hasOtherOption) {
      return omit(options, 0);
    }
    return options;
  }, [lenders, creditItem?.isEquifax, creditItem?.accountType]);

  const update = (values: any, disableIncrementUpdateCount: boolean = false) => {
    updateCreditItem(values, creditItem.id, CreditTypes.SECURED, disableIncrementUpdateCount);
  };

  const isShowMortgageAndArrears = useMemo(() => (addressType === 'security'
    && creditItem?.consolidationType
    && creditItem?.consolidationType !== ConsolidationType.Full
    && creditItem.accountType !== 'OtherProperty'
    && (creditItem.isEquifax || creditItem.hasContractualMonthlyPayments)
  ), [creditItem]);
  const isShowForm = !creditItem.ignored;
  const isShowAffordability = !!creditItem?.consolidationType;
  const isShowManualPaymentHistory = creditItem.isEquifax === false
    && !!creditItem?.consolidationType
    && creditItem.hasContractualMonthlyPayments;

  return (
    <>
      {creditItem
    && (
    <ModelForm
      model={creditItem}
      update={update}
      errors={errors}
      warnings={warnings}
    >
      <Container>
        {!creditItem.ignored && (
          <ConsolidationDetails data={creditItem} type="secured" />
        )}

        <Collapsible
          id={`${creditItem?.id}-credit-details`}
          title="Credit details"
          level={2}
          bodyCss={collapsibleWrapperCss}
        >

          {isShowForm && (
            <CreditDetailsContainer>
              {isShowAffordability && (
                <>
                  <SecuredRowDetails>
                    <ModelSelectSearchInput
                      field="lender"
                      label="Lender"
                      options={lenderOptions}
                      disabled={creditItem.isLender}
                      required
                    />

                    {(creditItem?.lender?.toString() === '0') ? (
                      <ModelInput
                        field="otherChargeHolderName"
                        label="Charge holder name"
                        required
                      />
                    ) : <EmptyInput />}

                  </SecuredRowDetails>

                  {creditItem?.isEquifax === false && (
                    <CreditDetailsRow>
                      <RadioYesNoInput
                        field="hasContractualMonthlyPayments"
                        label="Does it have monthly payments?"
                        disabled={creditItem.accountType === 'FirstCharge'}
                        required
                      />
                    </CreditDetailsRow>
                  )}
                </>
              )}

              {isShowMortgageAndArrears && (
                <>
                  <DetailsTitle>ARREARS</DetailsTitle>
                  <CreditDetailsRow>
                    <RadioYesNoInput
                      field="inArrears"
                      label="In arrears?"
                      disabled={creditItem.consolidationType === ConsolidationType.Arrears}
                      required
                    />
                    {
                      creditItem?.inArrears && (
                        <ModelNewNumericInput
                          field="amountOfArrears"
                          label="Amount of arrears"
                          prefix="£"
                          isFixedDecimal
                          required
                        />
                      )
                    }
                  </CreditDetailsRow>
                </>
              )}

              {isShowAffordability && (
                <>
                  <DetailsTitle>AFFORDABILITY</DetailsTitle>
                  <CreditDetailsRow>
                    <ModelNewNumericInput
                      field="actualBalance"
                      label="Actual balance"
                      prefix="£"
                      isFixedDecimal
                      required
                    />
                    <ModelNewNumericInput
                      id={`${creditItem.id}-actualMonthlyPayment`}
                      field="actualMonthlyPayment"
                      label="Actual monthly payment"
                      tooltip="This figure is from Equifax. Any amendments will require documentary evidence to support."
                      prefix="£"
                      isFixedDecimal
                      required
                    />
                  </CreditDetailsRow>
                  <CreditDetailsRow>
                    <ModelNewNumericInput
                      id={`${creditItem.id}-resultingBalance`}
                      field="resultingBalance"
                      label="Resulting balance"
                      tooltip="This figure is the balance that will be remaining on the account once any consolidations have been factored in."
                      disabled={[
                        ConsolidationType.Full,
                        ConsolidationType.NoConsolidation,
                      ].includes(ConsolidationType[creditItem?.consolidationType])}
                      prefix="£"
                      isFixedDecimal
                      required
                    />
                    <ModelNewNumericInput
                      field="resultingMonthlyPayment"
                      label="Resulting monthly payment"
                      prefix="£"
                      isFixedDecimal
                      disabled
                      required
                    />
                  </CreditDetailsRow>
                </>
              )}

              { isShowMortgageAndArrears && (
                <>
                  <DetailsTitle>MORTGAGE</DetailsTitle>
                  <CreditDetailsRow>
                    <ModelSelect
                      field="mortgageType"
                      label="Mortgage type"
                      options={MortgageType}
                      required
                    />
                    <EmptyInput />
                  </CreditDetailsRow>

                  {([
                    'PartAndPart',
                    'InterestOnly',
                  ].includes(creditItem?.mortgageType)) && (
                    <HighlightInputGroup>
                      <CreditDetailsRowHighlight>
                        <ModelSelect
                          field="repaymentVehicle"
                          label="Repayment vehicle"
                          options={RepaymentVehicleType}
                          required
                        />

                        { creditItem.repaymentVehicle === RepaymentVehicleType.Other
                          ? (
                            <ModelInput
                              field="repaymentVehicleDescription"
                              label="Repayment vehicle description"
                              required
                            />
                          )
                          : <EmptyInput />}
                      </CreditDetailsRowHighlight>
                      {(['PartAndPart'].includes(creditItem?.mortgageType)) && (
                        <>
                          <CreditDivider />
                          <CreditDetailsRowHighlight>
                            <ModelNewNumericInput
                              field="capitalBalance"
                              label="Capital balance"
                              prefix="£"
                              isFixedDecimal
                              required
                            />

                            <ModelNewNumericInput
                              field="interestOnlyBalance"
                              label="Interest only balance"
                              prefix="£"
                              isFixedDecimal
                              required
                            />
                          </CreditDetailsRowHighlight>
                        </>
                      )}
                    </HighlightInputGroup>
                  )}

                  { (['CapitalAndInterest'].includes(creditItem?.mortgageType)) && <CreditDivider />}

                  <CreditDetailsRow>
                    <ModelNewNumericInput
                      field="overallRemainingTerm"
                      label="Overall remaining term"
                      disabled={creditItem.isEquifax}
                      suffix="months"
                      required
                      integer
                      noGroupingSeperator
                    />
                    <ModelNewNumericInput
                      field="interestRate"
                      label="Interest rate"
                      suffix="%"
                      isFixedDecimal
                      required
                    />
                  </CreditDetailsRow>

                  <CreditDivider />

                  <CreditDetailsRow>
                    <RadioYesNoInput
                      field="isFixedRate"
                      label="Is it under fixed rate term?"
                      required
                    />

                    { creditItem.isFixedRate
                      ? (
                        <ModelNewNumericInput
                          field="remainingFixedTerm"
                          label="Remaining term of fixed rate?"
                          suffix="months"
                          integer
                          required
                          noGroupingSeperator
                        />
                      )
                      : <EmptyInput />}
                  </CreditDetailsRow>
                </>
              )}

              {isShowManualPaymentHistory && (
                <>
                  <DetailsTitle>PAYMENT HISTORY</DetailsTitle>

                  <CreditDetailsRow>
                    <ModelNewNumericInput
                      field="arrearsInLastTwelveMonths"
                      label="Highest level of arrears in last 12 months"
                      prefix="£"
                      isFixedDecimal
                      required
                    />
                    <EmptyInput />
                  </CreditDetailsRow>

                  {isNonEmptyNumber(creditItem.arrearsInLastTwelveMonths) && (
                    <ManualPaymentHistoryContainer>
                      <EquifaxAccordionHeaderShadow />
                      <CreditDetailsRow>
                        <ModelNewNumericInput
                          field="missedFullCMPLastTwelveMonths"
                          label="How many missed full CMPs in the last 12 months?"
                          disabled={
                            creditItem.arrearsInLastTwelveMonths < creditItem.actualMonthlyPayment
                            || creditItem.arrearsInLastTwelveMonths === 0
                          }
                          noGroupingSeperator
                          required
                          integer
                        />
                        {isNonEmptyNumber(creditItem.missedFullCMPLastTwelveMonths)
                          ? (
                            <ModelNewNumericInput
                              field="missedFullCMPLastSixMonths"
                              label="How many missed full CMPs in the last 6 months?"
                              disabled={
                                creditItem.missedFullCMPLastTwelveMonths === 0
                                || creditItem.missedFullCMPLastTwelveMonths === 12
                              }
                              noGroupingSeperator
                              required
                              integer
                            />
                          )
                          : <EmptyInput />}
                      </CreditDetailsRow>

                      {isNonEmptyNumber(creditItem.missedFullCMPLastSixMonths) && isNonEmptyNumber(creditItem.missedFullCMPLastTwelveMonths) && (
                        <>
                          <CreditDivider />
                          <CreditDetailsRow>
                            <ModelNewNumericInput
                              field="missedFullCMPLastThreeMonths"
                              label="How many missed full CMPs in the last 3 months?"
                              disabled={
                                creditItem.missedFullCMPLastSixMonths === 0
                                || creditItem.missedFullCMPLastSixMonths === 6
                              }
                              noGroupingSeperator
                              integer
                              required
                            />

                            {isNonEmptyNumber(creditItem.missedFullCMPLastThreeMonths)
                              ? (
                                <ModelNewNumericInput
                                  field="missedFullCMPLastTwoMonths"
                                  label="How many missed full CMPs in the last 2 months?"
                                  disabled={
                                    creditItem.missedFullCMPLastThreeMonths === 0
                                    || creditItem.missedFullCMPLastThreeMonths === 3
                                  }
                                  noGroupingSeperator
                                  integer
                                  required
                                />
                              )
                              : <EmptyInput />}
                          </CreditDetailsRow>
                        </>
                      )}
                    </ManualPaymentHistoryContainer>
                  )}

                  <CreditDetailsRow>
                    <RadioYesNoInput
                      field="inArrangement"
                      label="In arrangement?"
                      required
                    />

                    {creditItem.inArrangement && (
                      <ModelNewNumericInput
                        field="arrangementMonthlyPayment"
                        label="What is the arranged payment?"
                        prefix="£"
                        isFixedDecimal
                        required
                      />
                    )}
                  </CreditDetailsRow>
                </>
              )}
            </CreditDetailsContainer>
          )}

          {creditItem.isEquifax && (!isShowForm || !creditItem.consolidationType) && (
            <EquifaxAccordionHeaderShadow />
          )}

          <EquifaxDetails creditItem={creditItem} creditType="secured" />
        </Collapsible>
      </Container>
    </ModelForm>
    )}
    </>
  );
}

export default withExpandableRowErrors(SecuredCreditDetails);
